<template>
  <BasePopup :title="Lang.showColumns" :value="value" v-on="$listeners" width="1100">
    <div class="show-columns">
      <div class="show-columns__selection-wrapper">
        <div class="show-columns__selection">
          <div class="show-columns__selection-title">{{ Lang.dimensions }}</div>
          <div class="show-columns__selection-list">
            <div class="show-columns__selection-item" v-for="metric in metricsGroup('dimensions')" :key="metric.title">
              <v-checkbox
                hide-details
                v-model="selected"
                :label="metric.title"
                :value="metric"
                :class="{ 'disable-include': disableInclude, 'disable-exclude': disableExclude('dimensions') }"
              ></v-checkbox>
            </div>
          </div>
        </div>
        <div class="show-columns__selection">
          <div class="show-columns__selection-title">{{ Lang.dimensionBased }}</div>
          <div class="show-columns__selection-list">
            <div class="show-columns__selection-item" v-for="metric in metricsGroup('count')" :key="metric.title">
              <v-checkbox
                hide-details
                v-model="selected"
                :label="metric.title"
                :value="metric"
                :class="{ 'disable-include': disableInclude, 'disable-exclude': disableExclude('dimensions') }"
              ></v-checkbox>
            </div>
          </div>
        </div>
        <div class="show-columns__selection">
          <div class="show-columns__selection-title">{{ Lang.standardMetrics }}</div>
          <div class="show-columns__selection-list">
            <div class="show-columns__selection-item" v-for="metric in metricsGroup('standard')" :key="metric.title">
              <v-checkbox
                hide-details
                v-model="selected"
                :label="metric.title"
                :value="metric"
                :class="{ 'disable-exclude': disableExclude('standard') }"
              ></v-checkbox>
            </div>
          </div>
        </div>
        <div class="show-columns__selection">
          <div class="show-columns__selection-title">{{ Lang.advertiserMetrics }}</div>
          <div class="show-columns__selection-list">
            <div class="show-columns__selection-item" v-for="metric in metricsGroup('advertiser')" :key="metric.title">
              <v-checkbox
                hide-details
                v-model="selected"
                :label="metric.title"
                :value="metric"
                :class="{ 'disable-exclude': disableExclude('standard') }"
              ></v-checkbox>
            </div>
          </div>
        </div>
        <div class="show-columns__selection">
          <div class="show-columns__selection-title">{{ Lang.countableMetrics }}</div>
          <div class="show-columns__selection-list">
            <div class="show-columns__selection-item" v-for="metric in metricsGroup('calculated')" :key="metric.title">
              <v-checkbox
                hide-details
                v-model="selected"
                :label="metric.title"
                :value="metric"
                :class="{ 'disable-exclude': disableExclude('calculated') }"
              ></v-checkbox>
            </div>
          </div>
        </div>
      </div>
    </div>
    <template v-slot:footer>
      <ButtonSecondary @click="onCancel" class="show-columns__cancel" :tooltip="Lang.cancel">{{
        Lang.cancel
      }}</ButtonSecondary>
      <ButtonPrimary @click="onSaveColumns" class="show-columns__save" :tooltip="Lang.save">{{
        Lang.save
      }}</ButtonPrimary>
    </template>
  </BasePopup>
</template>

<script lang="ts">
import { intersection } from "lodash";
import Vue from "vue";

import ButtonPrimary from "@/components/customComponents/ButtonPrimary.vue";
import ButtonSecondary from "@/components/customComponents/ButtonSecondary.vue";
import BasePopup from "@/components/popups/BasePopup.vue";
import { TableHeader } from "@/components/widgets/explore/TableWidget.vue";
import { TABLE_GROUP_STANDARD, TABLE_SUBGROUP_ADVERTISER_METRICS } from "@/constants";
import { sortByNameAndOrder } from "@/helpers/dataMapping";
import Lang from "@/lang";
import inputsDictionary from "@/settings/inputsDictionary";
import { defaultTableHeaders } from "@/settings/tableSettings";
import { InputDictionary } from "@/types/common";

const MAX_DIMENSION = 4;
const MIN_DIMENSION = 1;
const MIN_MEASURES = 1;

export default Vue.extend({
  name: "ColumnsPopup",
  components: { ButtonPrimary, ButtonSecondary, BasePopup },
  props: { value: Boolean, selectedHeader: Array },

  data() {
    return {
      Lang,
      selected: defaultTableHeaders,
      beforeCancelSelected: [] as TableHeader[],
    };
  },

  watch: {
    value: {
      handler(val) {
        if (this.selectedHeader.length) {
          this.selected = [...(this.selectedHeader as TableHeader[])];
        }
        if (val) {
          this.beforeCancelSelected = this.selected;
        }
      },
    },
  },

  computed: {
    disableInclude(): boolean {
      const dimensions = this.metricsGroup("dimensions").map((item: InputDictionary) => item.value);
      const selected = this.selected.map((item: InputDictionary) => item.value);
      return intersection(dimensions, selected).length >= MAX_DIMENSION;
    },
  },

  methods: {
    onSaveColumns(): void {
      this.$emit("onColumnsChange", this.selected);
      this.$emit("input", false);
    },

    onCancel(): void {
      this.selected = this.beforeCancelSelected;
      this.$emit("input", false);
    },

    metricsGroup(group: string): InputDictionary[] {
      switch (group) {
        case "dimensions": {
          const dimensions = inputsDictionary.dimensionMetrics.concat(inputsDictionary.timeMetrics);

          return dimensions
            .filter((item: InputDictionary) => item?.table?.group === group)
            .sort(sortByNameAndOrder)
            .map((item: InputDictionary) => {
              return {
                title: item.title,
                value: item.value,
              };
            });
        }
        case "count": {
          return inputsDictionary.displayMetrics
            .filter((item: InputDictionary) => item?.isCountableMetric)
            .sort(sortByNameAndOrder)
            .map((item: InputDictionary) => {
              return {
                title: item.title,
                value: item.value,
              };
            });
        }
        case "advertiser": {
          return inputsDictionary.displayMetrics
            .filter(
              (item: InputDictionary) =>
                item?.table?.group === TABLE_GROUP_STANDARD &&
                item?.table?.subgroup === TABLE_SUBGROUP_ADVERTISER_METRICS
            )
            .sort(sortByNameAndOrder)
            .map((item: InputDictionary) => {
              return {
                title: item.title,
                value: item.value,
              };
            });
        }
        case "standard": {
          return inputsDictionary.displayMetrics
            .filter(
              (item: InputDictionary) =>
                item?.table?.group === group && item?.table?.subgroup !== TABLE_SUBGROUP_ADVERTISER_METRICS
            )
            .sort(sortByNameAndOrder)
            .map((item: InputDictionary) => {
              return {
                title: item.title,
                value: item.value,
              };
            });
        }
        default: {
          return inputsDictionary.displayMetrics
            .filter((item: InputDictionary) => item?.table?.group === group)
            .sort(sortByNameAndOrder)
            .map((item: InputDictionary) => {
              return {
                title: item.title,
                value: item.value,
              };
            });
        }
      }
    },

    disableExclude(group: string): boolean {
      const minSelected = group === "dimensions" ? MIN_DIMENSION : MIN_MEASURES;
      const dimensions = (group === "dimensions" ? this.metricsGroup(group) : inputsDictionary.displayMetrics).map(
        (item: InputDictionary) => item.value
      );

      const selected = this.selected.map((item: InputDictionary) => item.value);
      return intersection(dimensions, selected).length <= minSelected;
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/scss/theme.scss";
@import "@/scss/custom_variables.scss";

.show-columns {
  &__cancel {
    box-shadow: unset;
    margin-right: 8px;
  }
  &__selection-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    overflow-y: auto;
    max-height: calc(90vh - 230px);
    padding-right: 8px;
  }
  &__selection {
    align-self: stretch;
  }
  &__selection-title {
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: var(--v-primary-base);
    padding: 20px 0 8px;
  }
}
</style>

<style lang="scss">
.show-columns {
  .v-input--selection-controls {
    margin-top: 0;
  }
  .disable-include:not(.v-input--is-label-active) {
    pointer-events: none;
    opacity: 0.5;
  }
  .disable-exclude.v-input--is-label-active {
    pointer-events: none;
    opacity: 0.5;
  }
}
.v-application.theme--light,
.pdf-container.theme--light {
  .show-columns {
    .v-input--is-label-active .v-label {
      color: var(--v-content-dark-base) !important;
    }
  }
}
.v-application.theme--dark,
.pdf-container.theme--dark {
  .show-columns {
    .v-input--is-label-active .v-label {
      color: var(--v-content-light-base) !important;
    }
  }
}
</style>
